import _ from 'lodash'
import type { Update, Transform, Materializer } from '@wix/materializer'
import { createRef } from '@wix/materializer'
import { DataMapName, Component, pointers, Style } from '@wix/thunderbolt-becky-types'
import type { Batched, UpdateData, UpdateStructure, UpdateEnvironment } from '@wix/thunderbolt-catharsis'
import { removeHash, createRefResolver } from '@wix/thunderbolt-catharsis'
import type {
	ByRefsisOps,
	ByRefsisTransactionWithInvalidations,
	RawModel,
	WithNamespace,
	UpdateTranslation,
} from '../types'
import {
	getRefCreatorInNamespace,
	isLeafOverride,
	getLeafsOverridesMapAspectName,
	getQueryNameFromLeafsOverridesMapAspectName,
	getContextAndTemplateCompIdFromInflatedDataId,
	isSystemStyle,
} from './byRefUtils'
import {
	SCHEMAS,
	EMPTY_REMOVED,
	SITE_NAMESPACE,
	EMPTY_SCHEMA,
	DATA_MAP_NAMES,
	DATA_MAP_TO_QUERY,
	OVERRIDES_MAP_DELIMITER,
	QUERY_MAPPING,
	DATA_QUERY_TO_COMP_ID_ASPECT,
	ENVIRONMENT_NAMESPACE,
	RAW_TRANSLATIONS_ASPECT,
	RESOLVED_TRANSLATIONS_ASPECT,
	ORIGINAL_LANGUAGE_REF,
	LANGUAGE_REF,
	STRUCTURE_ASPECT,
	ROOT_COMP_ID_ASPECT,
	SYSTEM_STYLES,
	INVALIDATIONS_NAMESPACE,
} from './constants'
import { templateCompNodes, templateDataNodes } from './templateNodes'
import { defaultDataNodeToViewItem } from './templateNodes/dataNodes/utils/dataNodeUtils'

const DATA_ITEM_TYPES_TO_RESOLVE = new Set<string>(['RefArray', 'VariantRelation'])

const {
	data: {
		DATA_MAPS: { LAYOUT, STYLE, VARIABLES },
	},
	components: {
		PROPERTY_TYPES: { DATA_QUERY, DESIGN_QUERY },
	},
} = pointers

const DATA_MAP_TO_DATA_PREFIX: Record<string, string> = {
	[LAYOUT]: DATA_MAP_TO_QUERY.layout_data,
	[STYLE]: DATA_MAP_TO_QUERY.theme_data,
	[VARIABLES]: 'variables',
}

const getRefsForDataItem = createRefResolver(SCHEMAS)

export const getModelUpdateTransformer = (rawModel: RawModel): Transform => (value, path) => {
	const [namespace, aspect, id] = path

	if (
		namespace === ENVIRONMENT_NAMESPACE ||
		aspect.endsWith('LeafsOverridesMap') ||
		aspect === DATA_QUERY_TO_COMP_ID_ASPECT
	) {
		return value
	}

	if (aspect === RESOLVED_TRANSLATIONS_ASPECT) {
		const { originalLanguage, language } = value

		const shouldTranslatePageData = language !== originalLanguage
		const shouldTranslateWidgetData = namespace !== SITE_NAMESPACE && language !== 'en'
		if (!shouldTranslatePageData && !shouldTranslateWidgetData) {
			return
		}

		return _.get(rawModel, [namespace, RAW_TRANSLATIONS_ASPECT, language, id])
	}

	if (DATA_MAP_NAMES.has(aspect as DataMapName)) {
		const rawItem = _.get(rawModel, path)
		if (typeof rawItem === 'undefined') {
			return rawItem
		}

		if (templateDataNodes[aspect as DataMapName]?.toViewItem) {
			return templateDataNodes[aspect as DataMapName]!.toViewItem!({ rawItem, value })
		}

		return defaultDataNodeToViewItem(rawItem, value)
	}

	if (aspect === INVALIDATIONS_NAMESPACE) {
		return
	}

	// namespace is either 'site' or a templateKey
	const component = _.get(rawModel, [namespace, STRUCTURE_ASPECT, id])
	return component && templateCompNodes[aspect].toViewItem(component, value)
}

export const updateModel: ByRefsisTransactionWithInvalidations = (
	materializer: Materializer,
	rawModel: RawModel,
	modelUpdateFn: (ops: ByRefsisOps) => void
) => {
	const leafsOverridesToUpdate = new Map<string, Record<string, true>>()

	const updateDataItemIdToCompId = (
		batchUpdate: Update<void>,
		namespace: string,
		component: Component | undefined
	) => {
		if (!component) {
			return
		}

		const compId = component.id
		;[DATA_QUERY, DESIGN_QUERY].forEach((queryName) => {
			const dataQuery = component[queryName]
			if (dataQuery) {
				const dataQueryWithoutHash = removeHash(dataQuery)
				_.set(rawModel, [namespace, DATA_QUERY_TO_COMP_ID_ASPECT, dataQueryWithoutHash], compId)
				batchUpdate(namespace, DATA_QUERY_TO_COMP_ID_ASPECT, dataQueryWithoutHash, compId, EMPTY_SCHEMA)
			}
		})
	}

	const updateDataByNamespace: Batched<WithNamespace<UpdateData>> = (batchUpdate) => (
		namespace,
		dataMapName,
		id,
		dataItem
	) => {
		_.set(rawModel, [namespace, dataMapName, id], dataItem)
		if (!dataItem) {
			batchUpdate(namespace, dataMapName, id)
			return
		}

		const createRefInTemplateNamespace = getRefCreatorInNamespace(namespace)
		const customDeps = templateDataNodes[dataMapName]?.getDependencies(dataItem, createRefInTemplateNamespace)
		const schemaRefs = DATA_ITEM_TYPES_TO_RESOLVE.has(dataItem.type)
			? getRefsForDataItem(dataItem, dataMapName, namespace)
			: null
		const dependencies = schemaRefs || customDeps ? { schema: schemaRefs, custom: customDeps } : null
		batchUpdate(namespace, dataMapName, id, dependencies, { schema: dependencies, removed: EMPTY_REMOVED })

		const queryName = DATA_MAP_TO_QUERY[dataMapName]

		if (isLeafOverride(id)) {
			const dataPrefix = DATA_MAP_TO_DATA_PREFIX[dataMapName]
			const { templateCompId, context } = getContextAndTemplateCompIdFromInflatedDataId(id, dataPrefix)
			const overridesAspect = getLeafsOverridesMapAspectName(context, queryName)

			const overridesMapPath = [namespace, overridesAspect, templateCompId]
			const overridesMapStringPath = overridesMapPath.join(OVERRIDES_MAP_DELIMITER)

			if (!leafsOverridesToUpdate.has(overridesMapStringPath)) {
				const overridesMap = { ..._.get(rawModel, overridesMapPath) }
				leafsOverridesToUpdate.set(overridesMapStringPath, overridesMap)
			}

			leafsOverridesToUpdate.get(overridesMapStringPath)![id] = true
		}
	}

	const updateData: Batched<UpdateData> = (batchUpdate) => (dataMapName, id, dataItem) => {
		if (dataMapName === STYLE && dataItem && isSystemStyle((dataItem as unknown) as Style)) {
			_.set(rawModel, [SITE_NAMESPACE, SYSTEM_STYLES, id], true)
		}

		updateDataByNamespace(batchUpdate)(SITE_NAMESPACE, dataMapName, id, dataItem)
	}

	const updateTranslationByNamespace: Batched<WithNamespace<UpdateTranslation>> = (batchUpdate) => (
		namespace,
		language,
		documentDataId,
		documentDataItem
	) => {
		_.set(rawModel, [namespace, RAW_TRANSLATIONS_ASPECT, language, documentDataId], documentDataItem)

		if (!documentDataItem) {
			batchUpdate(namespace, RESOLVED_TRANSLATIONS_ASPECT, documentDataId)
			return
		}

		const dependencies = {
			originalLanguage: ORIGINAL_LANGUAGE_REF,
			language: LANGUAGE_REF,
		}

		batchUpdate(namespace, RESOLVED_TRANSLATIONS_ASPECT, documentDataId, dependencies, {
			schema: dependencies,
			removed: EMPTY_REMOVED,
		})
	}

	const updateTranslation: Batched<UpdateTranslation> = (batchUpdate) => (language, id, dataItem) =>
		updateTranslationByNamespace(batchUpdate)(SITE_NAMESPACE, language, id, dataItem)

	const updateStructureByNamespace: Batched<WithNamespace<UpdateStructure>> = (batchUpdate) => (
		namespace,
		id,
		component
	) => {
		_.set(rawModel, [namespace, STRUCTURE_ASPECT, id], component)
		updateDataItemIdToCompId(batchUpdate, namespace, component)

		for (const structureCalculator in templateCompNodes) {
			if (!component) {
				batchUpdate(namespace, structureCalculator, id)
				return
			}

			const dependencies = templateCompNodes[structureCalculator].getDependencies(
				component,
				getRefCreatorInNamespace(namespace)
			)

			if (!dependencies) {
				return
			}

			batchUpdate(namespace, structureCalculator, id, dependencies, {
				schema: dependencies,
				removed: EMPTY_REMOVED,
			})
		}
	}

	const updateWidgetRootCompId = (templateKey: string, rootCompId: string) => {
		_.set(rawModel, [templateKey, ROOT_COMP_ID_ASPECT], rootCompId)
	}

	const updateStructure: Batched<UpdateStructure> = (batchUpdate) => (id, component) =>
		updateStructureByNamespace(batchUpdate)(SITE_NAMESPACE, id, component)

	const updateEnvironment: Batched<UpdateEnvironment> = (batchUpdate) => (environment) => {
		for (const envType in environment) {
			for (const key in environment[envType]) {
				batchUpdate(ENVIRONMENT_NAMESPACE, envType, key, environment[envType][key], EMPTY_SCHEMA)
			}
		}
	}

	const invalidateWidget: Batched<ByRefsisOps['invalidateWidget']> = (batchUpdate) => (templateKey) => {
		batchUpdate(INVALIDATIONS_NAMESPACE, INVALIDATIONS_NAMESPACE, templateKey, _.uniqueId('invalidation_'))
	}

	const flushLeafsOverridesMapsUpdates = (batchUpdate: Update<void>) => {
		for (const [overridesMapStringPath, overridesMap] of leafsOverridesToUpdate) {
			const overridesMapPath = overridesMapStringPath.split(OVERRIDES_MAP_DELIMITER)
			const [overridesNamespace, overridesAspect, overridesCompId] = overridesMapPath
			const queryName = getQueryNameFromLeafsOverridesMapAspectName(overridesMapPath[1])
			_.set(rawModel, overridesMapPath, overridesMap)

			const overrideRefsMap = _.mapValues(overridesMap, (__, overrideId) =>
				createRef([overridesNamespace, QUERY_MAPPING[queryName], overrideId])
			)
			batchUpdate(overridesNamespace, overridesAspect, overridesCompId, overrideRefsMap, {
				schema: overrideRefsMap,
				removed: [],
			})
		}

		leafsOverridesToUpdate.clear()
	}

	return materializer.batch((batchUpdate) => {
		modelUpdateFn({
			updateData: updateData(batchUpdate),
			updateStructure: updateStructure(batchUpdate),
			updateTranslation: updateTranslation(batchUpdate),
			updateWidgetRootCompId,
			updateWidgetData: updateDataByNamespace(batchUpdate),
			updateWidgetStructure: updateStructureByNamespace(batchUpdate),
			updateWidgetTranslation: updateTranslationByNamespace(batchUpdate),
			updateEnvironment: updateEnvironment(batchUpdate),
			invalidateWidget: invalidateWidget(batchUpdate),
		})

		flushLeafsOverridesMapsUpdates(batchUpdate)
	})
}
